'use client';

import { ArrowLeft, ArrowRight } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useAnalytics } from '@/services/frontendAnalyticsService';

interface NavigationPost {
  title: string;
  slug: string;
  featuredImage: string | null;
  category: {
    name: string;
    slug: string;
  } | null;
}

interface BlogPostNavigationProps {
  postSlug: string;
  prev: NavigationPost | null;
  next: NavigationPost | null;
}

function truncateTitle(title: string, maxLength: number = 60): string {
  if (title.length <= maxLength) return title;
  return title.slice(0, maxLength).trimEnd() + '...';
}

export function BlogPostNavigation({ postSlug, prev, next }: BlogPostNavigationProps) {
  const analytics = useAnalytics();

  if (!prev && !next) return null;

  const handlePrevClick = () => {
    analytics.blog.featuresCTAClicked({
      postSlug,
      ctaType: 'post-nav',
      ctaLabel: 'Artikel Sebelumnya',
    });
  };

  const handleNextClick = () => {
    analytics.blog.featuresCTAClicked({
      postSlug,
      ctaType: 'post-nav',
      ctaLabel: 'Artikel Berikutnya',
    });
  };

  return (
    <nav className="relative px-4 py-12 md:px-8 lg:px-12 bg-white border-t border-neutral-100">
      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {prev ? (
            <Link
              href={`/blog/${prev.slug}`}
              onClick={handlePrevClick}
              className="group flex items-start gap-4 p-5 rounded-xl border border-neutral-200 hover:border-neutral-400 hover:bg-neutral-50 transition-all"
            >
              <div className="shrink-0 mt-1">
                <ArrowLeft className="w-4 h-4 text-neutral-400 group-hover:text-neutral-900 transition-colors" />
              </div>
              <div className="min-w-0 flex-1">
                <span className="text-xs font-medium text-neutral-500">
                  Artikel Sebelumnya
                </span>
                <h3 className="mt-1 text-sm font-semibold text-neutral-900 leading-snug line-clamp-2">
                  {truncateTitle(prev.title)}
                </h3>
                {prev.category && (
                  <span className="mt-2 inline-block text-xs text-neutral-500">
                    {prev.category.name}
                  </span>
                )}
              </div>
              {prev.featuredImage && (
                <div className="shrink-0 hidden sm:block w-16 h-16 rounded-lg overflow-hidden bg-neutral-100">
                  <Image
                    src={prev.featuredImage}
                    alt={prev.title}
                    width={64}
                    height={64}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </Link>
          ) : (
            <div />
          )}

          {next ? (
            <Link
              href={`/blog/${next.slug}`}
              onClick={handleNextClick}
              className="group flex items-start gap-4 p-5 rounded-xl border border-neutral-200 hover:border-neutral-400 hover:bg-neutral-50 transition-all text-right"
            >
              {next.featuredImage && (
                <div className="shrink-0 hidden sm:block w-16 h-16 rounded-lg overflow-hidden bg-neutral-100 order-first">
                  <Image
                    src={next.featuredImage}
                    alt={next.title}
                    width={64}
                    height={64}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <span className="text-xs font-medium text-neutral-500">
                  Artikel Berikutnya
                </span>
                <h3 className="mt-1 text-sm font-semibold text-neutral-900 leading-snug line-clamp-2">
                  {truncateTitle(next.title)}
                </h3>
                {next.category && (
                  <span className="mt-2 inline-block text-xs text-neutral-500">
                    {next.category.name}
                  </span>
                )}
              </div>
              <div className="shrink-0 mt-1">
                <ArrowRight className="w-4 h-4 text-neutral-400 group-hover:text-neutral-900 transition-colors" />
              </div>
            </Link>
          ) : (
            <div />
          )}
        </div>
      </div>
    </nav>
  );
}
